import { Link } from "react-router-dom";
import {
  Phone,
  Leaf,
  Building2,
  KeyRound,
  Snowflake,
  Wrench,
  Users,
  BadgeEuro,
  Zap,
  Layers,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import ZWImage from "@/components/ZWImage";
import ServiceArea from "@/components/ServiceArea";
import CTASection from "@/components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT, MONATSVERTRAEGE } from "@/data/services";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1734079692160-fcbe4be6ab96?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CRAFT_FALLBACK =
  "https://images.unsplash.com/photo-1689728318937-17d24bc0a65c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const SERVICES = [
  {
    icon: Leaf,
    title: "Gartenservice",
    desc: "Rasen, Hecke, Baumpflege & Saisonarbeiten – Ihr Garten in besten Händen.",
    prices: ["Rasen mähen ab 0,18 €/m²", "Heckenschnitt 5,00 €/lfm", "Pflege-Abo ab 99 €/Monat"],
    to: "/gartenservice",
    testid: "service-card-garten",
  },
  {
    icon: Building2,
    title: "Hausmeisterservice",
    desc: "Objektbetreuung, Treppenhaus & Kleinreparaturen für Eigentümer und Verwaltungen.",
    prices: ["Objektkontrolle 45,00 €/Std.", "Treppenhaus 3,00 €/m²", "Monatsverträge ab 199 €"],
    to: "/hausmeisterservice",
    testid: "service-card-hausmeister",
  },
  {
    icon: KeyRound,
    title: "Auszugs- & Übergabereinigung",
    desc: "Besenrein & übergabefertig – termingerecht und stressfrei für Mieter und Vermieter.",
    prices: ["1-Zimmer ab 149 €", "3-Zimmer ab 279 €", "Einfamilienhaus ab 499 €"],
    to: "/auszugsreinigung",
    testid: "service-card-auszug",
  },
  {
    icon: Snowflake,
    title: "Winterdienst",
    desc: "Schneeräumung & Streudienst – zuverlässig, pünktlich, privat wie gewerblich.",
    prices: ["Einsatz ab 49 €", "Streudienst 15 €/Einsatz", "Saisonvertrag ab 350 €"],
    to: "/winterdienst",
    testid: "service-card-winter",
  },
  {
    icon: Wrench,
    title: "Weitere Leistungen",
    desc: "Umzug, Möbelmontage, Entrümpelung, Fassade, Dachrinne & Graffitientfernung.",
    prices: ["Umzugsservice 45 €/Std.", "Fassade 5,00 €/m²", "Dachrinne 4,00 €/lfm"],
    to: "/weitere-leistungen",
    testid: "service-card-weitere",
  },
];

const PRINCIPLES = [
  {
    icon: Users,
    title: "Persönlich vor Ort",
    text: "Zwei Brüder, feste Ansprechpartner. Sie sprechen direkt mit demjenigen, der auf der Leiter oder am Rasenmäher steht.",
  },
  {
    icon: BadgeEuro,
    title: "Festpreise ohne Überraschung",
    text: "Alle Leistungs- und Quadratmeterpreise sind fix kalkuliert. Vorab wissen, was am Ende auf der Rechnung steht.",
  },
  {
    icon: Zap,
    title: "Schnell & zuverlässig",
    text: "Kurzfristige Einsätze bei Sturmschäden, Mieterwechseln oder plötzlichem Wintereinbruch sind für uns selbstverständlich.",
  },
  {
    icon: Layers,
    title: "Alles aus einer Hand",
    text: "Garten, Hausmeisterservice, Entrümpelung, Treppenhaus und Winterdienst – ein Anruf deckt die gesamte Immobilie ab.",
  },
];

const FAQS = [
  {
    q: "Gibt es einen Mindestauftragswert?",
    a: "Ja, der Mindestauftragswert beträgt für Privatkunden 69,00 € netto (82,11 € brutto) und für Gewerbekunden 99,00 € netto (117,81 € brutto).",
  },
  {
    q: "Wie wird die Anfahrt berechnet?",
    a: "Im Umkreis von 20 Kilometern um unseren Standort in Herten ist die Anfahrt vollständig im Leistungspreis enthalten. Für Entfernungen darüber hinaus berechnen wir 0,70 € pro Kilometer.",
  },
  {
    q: "Wie lauten die Zahlungsbedingungen?",
    a: "Sie erhalten nach getaner Arbeit eine ordnungsgemäße Handwerkerrechnung. Das Zahlungsziel beträgt 7 Tage.",
  },
  {
    q: "Kommen wirklich die Inhaber selbst?",
    a: "Ja. Wir arbeiten als Brüder-Team persönlich vor Ort. Keine Weitergabe an Subunternehmer oder wechselnde Hilfskräfte.",
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="ZWILLINGSWERK – Garten- & Hausmeisterservice Herten | Feste Preise, 20 km Anfahrt frei"
        description="Gartenpflege, Hausmeisterservice, Auszugsreinigung & Winterdienst in Herten + 20 km. Zwei Brüder, transparente Festpreise (netto/brutto), Anfahrt inklusive. ☎ 0176 209 39 646"
      />

      {/* Hero: Split-Struktur */}
      <section className="py-12 lg:py-20 bg-gradient-to-b from-white to-[#F2F7F2] border-b border-[#D7E5D9]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6 animate-in">
              <div className="inline-flex items-center gap-2 bg-[#E6F3E6] border border-[#B9E0BD] text-[#1E5631] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Aus Herten für Herten &amp; 20 km Umkreis
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#122B18] tracking-tight leading-none font-display">
                Zwei Brüder. Saubere Arbeit.
              </h1>
              <p className="text-lg sm:text-xl text-[#3A523E] leading-relaxed max-w-2xl">
                Wir stehen persönlich vor Ort auf Ihrem Grundstück – ohne wechselnde Subunternehmer. Gartenpflege,
                Objektbetreuung, Auszugsreinigung und Winterdienst mit transparenten Festpreisen ab der ersten Minute.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2" data-testid="trust-badges">
                <div className="bg-white p-3 rounded-lg border border-[#D7E5D9] shadow-sm">
                  <div className="text-[#1E5631] font-bold text-sm">20 km Anfahrt frei</div>
                  <div className="text-xs text-[#5D7761]">Ab Johannesstr. 20, Herten</div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-[#D7E5D9] shadow-sm">
                  <div className="text-[#1E5631] font-bold text-sm">Feste Netto/Brutto-Preise</div>
                  <div className="text-xs text-[#5D7761]">Keine versteckten Zuschläge</div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-[#D7E5D9] shadow-sm col-span-2 sm:col-span-1">
                  <div className="text-[#1E5631] font-bold text-sm">Direktkontakt</div>
                  <div className="text-xs text-[#5D7761]">{CONTACT.hours} erreichbar</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="hero-call-button"
                  className="inline-flex justify-center items-center gap-3 bg-[#1E5631] hover:bg-[#154023] text-white text-base font-bold px-7 py-4 rounded-lg shadow-md transition-colors"
                >
                  <Phone className="w-5 h-5" /> Jetzt anrufen: {CONTACT.phoneDisplay}
                </a>
                <Link
                  to="/gartenservice"
                  data-testid="hero-prices-button"
                  className="inline-flex justify-center items-center bg-white hover:bg-[#F2F7F2] text-[#1E5631] border-2 border-[#1E5631] text-base font-bold px-6 py-4 rounded-lg transition-colors"
                >
                  Preise ansehen
                </Link>
              </div>
              <p className="text-xs text-[#6B856F]">
                Mindestauftragswert: Privat 69 € · Gewerbe 99 € netto. Zahlungsziel 7 Tage.
              </p>
            </div>

            <div className="lg:col-span-5 animate-in-d1">
              <div className="bg-white rounded-2xl border-2 border-[#D7E5D9] p-6 shadow-xl space-y-5">
                <div className="flex items-center justify-between border-b border-[#E6EFE7] pb-4 gap-3">
                  <div>
                    <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">
                      Werkbank &amp; Fuhrpark
                    </span>
                    <h2 className="text-lg font-black text-[#122B18] font-display">Einsatzbereit im Revier</h2>
                  </div>
                  <span className="bg-[#E6F3E6] text-[#1E5631] text-xs font-black px-2.5 py-1 rounded whitespace-nowrap">
                    Kurzfristige Einsätze
                  </span>
                </div>

                <ZWImage
                  src="/images/hero.png"
                  fallback={HERO_FALLBACK}
                  alt="Zwei Brüder vom ZWILLINGSWERK-Team in grüner Arbeitskleidung bei der Gartenarbeit in Herten"
                  className="w-full aspect-[4/3] rounded-xl shadow-inner"
                  subcaption="ZWILLINGSWERK Herten"
                  caption="Eigenes Profi-Gerät für Grünanlage, Winterdienst & Haustechnik"
                  testid="hero-image"
                />

                <div className="bg-[#F2F7F2] rounded-xl p-4 border border-[#D7E5D9] space-y-2">
                  <div className="text-xs font-bold text-[#122B18] uppercase">Beliebte Festpreis-Richtwerte:</div>
                  <div className="flex justify-between text-xs py-1 border-b border-[#DDE7DE]">
                    <span>Rasenmähen (pro m²)</span>
                    <span className="font-bold text-[#1E5631]">
                      0,18 € netto <span className="text-[10px] text-[#5D7761]">(0,21 € brutto)</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-xs py-1 border-b border-[#DDE7DE]">
                    <span>Heckenschnitt bis 2 m (pro lfm)</span>
                    <span className="font-bold text-[#1E5631]">
                      5,00 € netto <span className="text-[10px] text-[#5D7761]">(5,95 € brutto)</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-xs py-1">
                    <span>Objektbetreuung (Std.)</span>
                    <span className="font-bold text-[#1E5631]">
                      45,00 € netto <span className="text-[10px] text-[#5D7761]">(53,55 € brutto)</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leistungsübersicht */}
      <section className="py-16 bg-white border-b border-[#D7E5D9]" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-3 mb-12">
            <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">
              Vollständige Preistransparenz
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-[#122B18] tracking-tight font-display">
              Unsere Leistungen &amp; Festpreise
            </h2>
            <p className="text-[#4A634E] text-base">
              Gültig im gesamten 20 km Radius ab Herten. Netto für Gewerbe &amp; Verwaltungen, Brutto (inkl. 19 % MwSt.)
              für Privatkunden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={s.testid}
                className="group bg-[#F8FAF8] hover:bg-white border border-[#D7E5D9] hover:border-[#1E5631] rounded-xl p-6 space-y-4 transition-colors shadow-sm hover:shadow-md flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-[#1E5631] text-white flex items-center justify-center shrink-0">
                    <s.icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-black text-[#122B18] font-display leading-tight">{s.title}</h3>
                </div>
                <p className="text-sm text-[#4A634E] leading-relaxed">{s.desc}</p>
                <ul className="space-y-1.5 text-xs text-[#1B281B]">
                  {s.prices.map((p) => (
                    <li key={p} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3D8B4D] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-[#1E5631] pt-2">
                  Alle Preise ansehen
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}

            {/* Kontakt-Karte als 6. Kachel */}
            <a
              href={CONTACT.phoneHref}
              data-testid="service-card-kontakt"
              className="group bg-[#122B18] border border-[#1E5631] rounded-xl p-6 space-y-4 transition-colors hover:bg-[#154023] flex flex-col"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-[#3D8B4D] text-white flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-black text-white font-display leading-tight">Direkt anrufen</h3>
              </div>
              <p className="text-sm text-[#D7E5D9] leading-relaxed">
                Kein Formular, keine Warteschleife: Sie sprechen sofort mit einem der beiden Inhaber.
              </p>
              <div className="text-2xl font-black text-white font-display">{CONTACT.phoneDisplay}</div>
              <div className="text-xs text-[#A3D9A5]">{CONTACT.hours}</div>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-[#A3D9A5] pt-2">
                Jetzt durchwählen
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Brüder-Prinzip */}
      <section className="py-16 bg-[#F2F7F2] border-b border-[#D7E5D9]" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">Das ZWILLINGSWERK-Prinzip</span>
            <h2 className="text-3xl sm:text-4xl font-black text-[#122B18] tracking-tight font-display">
              Warum unsere Kunden uns persönlich anrufen
            </h2>
            <p className="text-[#4A634E] text-base sm:text-lg">
              Kein anonymer Vermittlungsdienst, keine ständig wechselnden Aushilfen. Wenn Sie ZWILLINGSWERK beauftragen,
              bekommen Sie genau die zwei Handwerker, die mit ihrem Namen dafür geradestehen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {PRINCIPLES.map((p) => (
              <div
                key={p.title}
                className="bg-white border border-[#D7E5D9] rounded-xl p-6 space-y-3 shadow-sm"
                data-testid={`principle-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#E6F3E6] text-[#1E5631] flex items-center justify-center">
                  <p.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-[#122B18]">{p.title}</h3>
                <p className="text-sm text-[#4A634E] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf in 3 Schritten */}
      <section className="py-16 bg-white border-b border-[#D7E5D9]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <ZWImage
                src="/images/gartenservice.png"
                fallback={CRAFT_FALLBACK}
                alt="Heckenschneider und sauberer Gartenweg – Präzisionsarbeit von ZWILLINGSWERK"
                className="w-full aspect-[4/3] rounded-2xl shadow-lg"
                subcaption="Präzisionsarbeit"
                caption="Saubere Kanten & geräumte Wege"
                testid="process-image"
              />
            </div>
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">Klare Abläufe</span>
              <h2 className="text-3xl sm:text-4xl font-black text-[#122B18] font-display">
                In 3 Schritten zu erledigter Arbeit
              </h2>
              <div className="space-y-4">
                {[
                  {
                    n: "1",
                    t: "Kurzer Anruf oder Nachricht",
                    d: "Sie schildern Ihr Anliegen (z. B. Hecke schneiden 20 lfm oder Wohnungsübergabe). Wir nennen sofort den Festpreisrahmen.",
                  },
                  {
                    n: "2",
                    t: "Termintreue Ausführung vor Ort",
                    d: "Die beiden Brüder rücken pünktlich mit eigenem Werkzeug an. 20 km Anfahrt ab Herten sind im Preis enthalten.",
                  },
                  {
                    n: "3",
                    t: "Abnahme & transparente Rechnung",
                    d: "Gemeinsame Prüfung des Ergebnisses. Sie erhalten eine ordentliche Handwerkerrechnung mit 7 Tagen Zahlungsziel.",
                  },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-4 p-4 bg-[#F8FAF8] rounded-xl border border-[#D7E5D9]">
                    <div className="w-8 h-8 rounded-full bg-[#1E5631] text-white flex items-center justify-center font-bold text-sm shrink-0">
                      {s.n}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#122B18] text-base">{s.t}</h3>
                      <p className="text-xs sm:text-sm text-[#4A634E]">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hausmeister-Monatsverträge (Teaser) */}
      <section className="py-16 bg-[#F2F7F2] border-b border-[#D7E5D9]" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">
              Planbarkeit für Vermieter &amp; Firmen
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-[#122B18] font-display">Hausmeister-Monatsverträge</h2>
            <p className="text-[#4A634E] text-base">
              Feste monatliche Betreuung ohne Personalaufwand. Alle Preise zzgl. 19 % MwSt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {MONATSVERTRAEGE.map((p) => (
              <div
                key={p.name}
                data-testid={`package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-2xl p-6 flex flex-col justify-between ${
                  p.highlight
                    ? "border-2 border-[#1E5631] bg-white shadow-xl relative"
                    : "border-2 border-[#D7E5D9] bg-white"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#1E5631] text-white text-xs font-black uppercase tracking-wider px-4 py-1 rounded-full whitespace-nowrap">
                    Beliebteste Wahl
                  </div>
                )}
                <div className="space-y-4">
                  <div className="flex justify-between items-center gap-2 pt-1">
                    <h3 className="text-xl font-black text-[#122B18] font-display">{p.name}</h3>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded whitespace-nowrap ${
                        p.highlight ? "bg-[#1E5631] text-white" : "bg-[#E6F3E6] text-[#1E5631]"
                      }`}
                    >
                      {p.tag}
                    </span>
                  </div>
                  <div className="py-2">
                    <span className="text-4xl font-black text-[#1E5631] font-display">{p.price}</span>
                    <span className="text-xs text-[#5D7761]"> / Monat zzgl. MwSt.</span>
                  </div>
                  <ul className="space-y-2.5 text-xs sm:text-sm text-[#3A523E]">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <span className="text-[#1E5631] font-black">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={CONTACT.phoneHref}
                  data-testid={`${p.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                  className={`mt-6 block text-center font-bold py-3 rounded-lg text-sm transition-colors ${
                    p.highlight
                      ? "bg-[#1E5631] hover:bg-[#154023] text-white shadow-md"
                      : "bg-white hover:bg-[#F2F7F2] text-[#1E5631] border border-[#1E5631]"
                  }`}
                >
                  {p.name.replace("Paket ", "")} anfragen
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#5D7761] mt-6">
            Details und alle Einzelleistungen:{" "}
            <Link to="/hausmeisterservice" className="text-[#1E5631] font-bold underline" data-testid="packages-more-link">
              Hausmeisterservice im Überblick
            </Link>
          </p>
        </div>
      </section>

      {/* Einsatzgebiet */}
      <ServiceArea />

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAF8] border-b border-[#D7E5D9]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">Transparenz im Vorfeld</span>
            <h2 className="text-3xl font-black text-[#122B18] font-display">Häufig gestellte Fragen</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="p-5 rounded-xl bg-white border border-[#D7E5D9]"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-bold text-[#122B18] text-sm sm:text-base py-0 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-xs sm:text-sm text-[#4A634E] pt-3">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection />
    </>
  );
}
